// What each register expects in a spreadsheet.
//
// One description per register, used three times over: to read the file's header row, to write the
// template somebody starts from, and to tell them on screen what the columns are. Keeping those
// three in one place is the point - a template that disagrees with the reader is a support call.
//
// Column names are matched with punctuation and case thrown away, so "Vehicle Number", "vehicle
// number" and "vehicle_number" are the same column. People write headers the way they write them.

export const columnKey = (name) => String(name || '').toLowerCase().replace(/[^a-z0-9]/g, '');

const spec = (kind, title, note, columns, required, sample) => ({
  kind,
  title,
  note,
  columns,
  required,
  sample,
  // The header a person reads, against the field the server knows it by.
  keys: Object.fromEntries(Object.entries(columns).map(([header, field]) => [columnKey(header), field])),
  headers: Object.keys(columns),
});

export const IMPORTS = {
  vehicles: spec(
    'vehicles',
    'Import vehicles',
    'Each row registers a truck, the driver named on it, and any tag or GPS unit already fitted to it - the same as filling in the registration screen once per row.',
    {
      vehicle_number: 'name',
      vehicle_type: 'vehicleType',
      owner: 'owner',
      transporter: 'transporter',
      driver_name: 'driverName',
      driver_mobile: 'driverMobile',
      rfid: 'rfid',
      gps: 'gps',
      tracking_reference: 'providerId',
      registered_transporter: 'registeredTransporter',
      hire: 'hire',
    },
    ['vehicle_number', 'owner', 'transporter', 'driver_name', 'driver_mobile'],
    [
      {
        vehicle_number: 'KA 01 AB 1234', vehicle_type: 'Truck', owner: 'Shree Logistics', transporter: 'Shree Logistics', driver_name: 'Ravi Shankar', driver_mobile: '9845000001', rfid: 'TAG-0001', gps: '', tracking_reference: '', registered_transporter: 'Shree Logistics', hire: 'OWNED',
      },
      {
        vehicle_number: 'KA 02 CD 5678', vehicle_type: 'Tanker', owner: 'Own fleet', transporter: 'Anchepalya Carriers', driver_name: 'Mohan Das', driver_mobile: '9845000002', rfid: '', gps: 'GPS-0001', tracking_reference: '', registered_transporter: '', hire: 'MARKET',
      },
    ],
  ),
  drivers: spec(
    'drivers',
    'Import drivers',
    'The reference identifies the driver and is usually their phone number. A reference already on the register, or twice in the file, is refused.',
    { name: 'name', reference: 'reference', mobile: 'mobile' },
    ['name', 'reference'],
    [
      { name: 'Ravi Shankar', reference: '9845000001', mobile: '9845000001' },
      { name: 'Mohan Das', reference: '9845000002', mobile: '9845000002' },
    ],
  ),
  transporters: spec(
    'transporters',
    'Import transporters',
    'Carriers the plant hauls with. Load these before the vehicles, so a truck can be registered against one.',
    {
      name: 'name', contact_name: 'contactName', contact_mobile: 'contactMobile', gstin: 'gstin',
    },
    ['name'],
    [
      {
        name: 'Shree Logistics', contact_name: 'R Kumar', contact_mobile: '9845000001', gstin: '29ABCDE1234F1Z5',
      },
      {
        name: 'Anchepalya Carriers', contact_name: '', contact_mobile: '9845000002', gstin: '',
      },
    ],
  ),
  assignments: spec(
    'assignments',
    'Attach devices to vehicles',
    'The truck is named by its number plate and the device by what is printed on it; both must already be registered. Mode is PERMANENT for a unit fitted to the truck, TEMPORARY for one issued for a trip, and is TEMPORARY if left blank.',
    {
      vehicle_number: 'vehicle', device_identifier: 'device', type: 'kind', mode: 'mode',
    },
    ['vehicle_number', 'device_identifier'],
    [
      {
        vehicle_number: 'KA 01 AB 1234', device_identifier: 'TAG-0001', type: 'RFID', mode: 'PERMANENT',
      },
      {
        vehicle_number: 'KA 02 CD 5678', device_identifier: 'GPS-0001', type: 'GPS', mode: 'TEMPORARY',
      },
    ],
  ),
};

export const importable = (kind) => Object.prototype.hasOwnProperty.call(IMPORTS, kind);

// Turns the parsed rows of a file into what the server reads, keeping each row's line number so a
// refusal can name the line the person has to go and fix.
export const readRows = (kind, table) => {
  const { keys, required } = IMPORTS[kind];
  const [header = [], ...lines] = table;
  const mapped = header.map((name) => keys[columnKey(name)]);
  const missing = required.filter((column) => !mapped.includes(keys[columnKey(column)]));
  if (missing.length) return { missing };
  return {
    rows: lines
      .map((cells, index) => ({ cells, line: index + 2 }))
      .filter(({ cells }) => cells.some((cell) => String(cell || '').trim()))
      .map(({ cells, line }) => {
        const row = { line };
        mapped.forEach((field, index) => { if (field) row[field] = String(cells[index] || '').trim(); });
        return row;
      }),
  };
};
